import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import {
  Button,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material'
import _ from 'lodash'

import {
  CHART_CARD_BACKGROUND,
  INPUT_BORDER,
  INPUT_RADIUS_PX,
  NEUTRAL_GREY_500,
} from '@/common/constants'
import { SECONDARY_BUTTON_SX } from '@/common/styles'
import { generateEnumTranslations } from '@/common/utils/enum'
import { useFormatCurrency } from '@/common/utils/formatting'
import { toHHMM } from '@/common/utils/time'
import { SpatialPolicy as Policy } from '@/models/spatialPolicy'
import { generateEnumDropdownOptions } from '@/models/spatialPolicyTypes'
import { VariableDuration, VariablePriceDuration } from '@/models/variablePriceDuration'
import {
  Day,
  DayEnum,
  VariablePriceSchedule,
  VariableSchedule,
} from '@/models/variablePriceSchedule'
import { useCurrentRegion } from '@/modules/urlRouting/hooks'

import { EditPolicy } from '../../../policyEditController'
import { EditableFieldProps } from '../../PolicyDetails/Form/fields/types'

import { CurrencyField, ThemedTimeField } from './inputs'
import { ThemedField } from './ThemedField'

/** The same look as the Policies Library table: a white card with a light header row. */
const TABLE_CONTAINER_SX = {
  bgcolor: 'common.white',
  border: `1px solid ${INPUT_BORDER}`,
  borderRadius: `${INPUT_RADIUS_PX}px`,
  '& .MuiTableCell-head': {
    bgcolor: CHART_CARD_BACKGROUND,
    color: NEUTRAL_GREY_500,
    fontSize: '12px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  '& .MuiTableRow-root:last-of-type .MuiTableCell-body': { borderBottom: 'none' },
}

/** A whole number of minutes, as the old duration inputs. An empty field is `null`. */
const parseMinutes = (value: string): number | null => {
  const parsed = parseInt(value, 10)
  return Number.isNaN(parsed) ? null : parsed
}

interface RowProps<T> {
  editable: boolean
  index: number
  row: T
  /** Changes this row on the policy being edited. */
  editRow: (mutate: (row: T) => void) => void
  /** Missing on the first row: a timetable always has one row. */
  onRemove?: () => void
}

const RemoveCell: FC<{ editable: boolean; index: number; onRemove?: () => void }> = ({
  editable,
  index,
  onRemove,
}) => {
  const { t } = useTranslation()
  if (!editable) return null
  return (
    <TableCell padding="checkbox">
      {onRemove && (
        <IconButton
          size="small"
          aria-label={t('policiesLibrary.removeTimeFrame', 'Remove time frame {{number}}', {
            number: index + 1,
          })}
          onClick={onRemove}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </TableCell>
  )
}

const ScheduleRow: FC<RowProps<VariableSchedule>> = ({
  editable,
  index,
  row,
  editRow,
  onRemove,
}) => {
  const { t } = useTranslation()
  const {
    data: { currencyCode },
  } = useCurrentRegion()
  const formatCurrency = useFormatCurrency(currencyCode)
  const dayNames = generateEnumTranslations(DayEnum, 'DayEnum')
  const { day, startTime, endTime, price } = row

  if (!editable)
    return (
      <TableRow>
        <TableCell>{toHHMM(startTime ?? undefined)}</TableCell>
        <TableCell>{toHHMM(endTime ?? undefined)}</TableCell>
        <TableCell>{formatCurrency(price)}</TableCell>
        <TableCell>{dayNames[day]}</TableCell>
      </TableRow>
    )

  return (
    <TableRow>
      <TableCell>
        <ThemedTimeField
          ariaLabel={t('policiesLibrary.formStartTime', 'Start Time (24hr)')}
          value={startTime}
          onChange={time => editRow(r => (r.startTime = time ?? null))}
        />
      </TableCell>
      <TableCell>
        <ThemedTimeField
          ariaLabel={t('policiesLibrary.formEndTime', 'End Time (24hr)')}
          value={endTime}
          onChange={time => editRow(r => (r.endTime = time ?? null))}
        />
      </TableCell>
      <TableCell>
        <CurrencyField
          currencyCode={currencyCode}
          ariaLabel={t('policiesLibrary.formPricePerHour', 'Price (Per Hour)')}
          value={price === 0 ? null : price}
          onChange={value => editRow(r => (r.price = Number(value) || 0))}
          sx={{ minWidth: 110 }}
        />
      </TableCell>
      <TableCell>
        <TextField
          select
          size="small"
          fullWidth
          slotProps={{
            htmlInput: {
              'aria-label': t('policiesLibrary.formDaysOfWeekTitle', 'Days Of The Week'),
            },
          }}
          value={day}
          onChange={event => editRow(r => (r.day = event.target.value as Day))}
          sx={{ minWidth: 140 }}
        >
          {generateEnumDropdownOptions(DayEnum, 'DaysOfWeek').map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.text}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>
      <RemoveCell editable={editable} index={index} onRemove={onRemove} />
    </TableRow>
  )
}

const DurationRow: FC<RowProps<VariableDuration>> = ({
  editable,
  index,
  row,
  editRow,
  onRemove,
}) => {
  const { t } = useTranslation()
  const {
    data: { currencyCode },
  } = useCurrentRegion()
  const formatCurrency = useFormatCurrency(currencyCode)
  const { startDuration, endDuration, price } = row
  const minutesText = (minutes: number | null) => (minutes || minutes === 0 ? minutes : '')

  if (!editable)
    return (
      <TableRow>
        <TableCell>{minutesText(startDuration)}</TableCell>
        <TableCell>{minutesText(endDuration)}</TableCell>
        <TableCell>{formatCurrency(price)}</TableCell>
      </TableRow>
    )

  return (
    <TableRow>
      <TableCell>
        <TextField
          type="number"
          size="small"
          fullWidth
          slotProps={{
            htmlInput: { 'aria-label': t('policiesLibrary.formStartTimeMin', 'Start Time (min)') },
          }}
          value={minutesText(startDuration)}
          onChange={event => editRow(r => (r.startDuration = parseMinutes(event.target.value)))}
        />
      </TableCell>
      <TableCell>
        <TextField
          type="number"
          size="small"
          fullWidth
          slotProps={{
            htmlInput: { 'aria-label': t('policiesLibrary.formEndTimeMin', 'End Time (min)') },
          }}
          value={minutesText(endDuration)}
          onChange={event => editRow(r => (r.endDuration = parseMinutes(event.target.value)))}
        />
      </TableCell>
      <TableCell>
        <CurrencyField
          currencyCode={currencyCode}
          ariaLabel={t('policiesLibrary.formPricePerHour', 'Price (Per Hour)')}
          value={price === 0 ? null : price}
          onChange={value => editRow(r => (r.price = Number(value) || 0))}
          sx={{ minWidth: 110 }}
        />
      </TableCell>
      <RemoveCell editable={editable} index={index} onRemove={onRemove} />
    </TableRow>
  )
}

interface TimetableProps {
  editable: boolean
  editPolicy?: EditPolicy
  policy: Policy
}

/**
 * The variable prices of a parking fee policy, by time of day or by parking duration. It does
 * the same as the old `TimeTable`.
 */
const Timetable: FC<TimetableProps> = ({ editable, editPolicy, policy }) => {
  const { t } = useTranslation()
  const { parkingFeeType, variablePriceDuration, variablePriceSchedule } = policy

  const isSchedule = parkingFeeType === 'variable_price_schedule'
  const prices = isSchedule
    ? variablePriceSchedule?.prices
    : parkingFeeType === 'variable_price_parking_duration'
      ? variablePriceDuration?.prices
      : undefined
  if (!prices) return null

  const headers = isSchedule
    ? [
        t('policiesLibrary.formStartTime', 'Start Time (24hr)'),
        t('policiesLibrary.formEndTime', 'End Time (24hr)'),
        t('policiesLibrary.formPricePerHour', 'Price (Per Hour)'),
        t('policiesLibrary.formDaysOfWeekTitle', 'Days Of The Week'),
      ]
    : [
        t('policiesLibrary.formStartTimeMin', 'Start Time (min)'),
        t('policiesLibrary.formEndTimeMin', 'End Time (min)'),
        t('policiesLibrary.formPricePerHour', 'Price (Per Hour)'),
      ]

  const remove = (index: number) =>
    index === 0 ? undefined : () => editPolicy?.(p => p.variablePrices?.remove(index))

  return (
    <>
      <TableContainer sx={TABLE_CONTAINER_SX}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {headers.map(header => (
                <TableCell key={header}>{header}</TableCell>
              ))}
              {editable && <TableCell padding="checkbox" />}
            </TableRow>
          </TableHead>
          <TableBody>
            {prices.map((row, index) =>
              isSchedule ? (
                <ScheduleRow
                  key={index}
                  editable={editable}
                  index={index}
                  row={row as VariableSchedule}
                  editRow={mutate =>
                    editPolicy?.(p => {
                      const editedRow = (p.variablePrices as VariablePriceSchedule)?.prices[index]
                      if (editedRow) mutate(editedRow)
                    })
                  }
                  onRemove={remove(index)}
                />
              ) : (
                <DurationRow
                  key={index}
                  editable={editable}
                  index={index}
                  row={row as VariableDuration}
                  editRow={mutate =>
                    editPolicy?.(p => {
                      const editedRow = (p.variablePrices as VariablePriceDuration)?.prices[index]
                      if (editedRow) mutate(editedRow)
                    })
                  }
                  onRemove={remove(index)}
                />
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {editable && (
        <Button
          startIcon={<AddIcon />}
          onClick={() => editPolicy?.(p => p.variablePrices?.add())}
          sx={{ ...SECONDARY_BUTTON_SX, alignSelf: 'flex-start', mt: 1 }}
        >
          {t('policiesLibrary.formAddTimeFrame', 'Add Time Frame')}
        </Button>
      )}
    </>
  )
}

export const VariablePricesField: FC<EditableFieldProps> = ({
  editable = false,
  editPolicy,
  policy,
  ...props
}) => {
  const { t } = useTranslation()

  return (
    <ThemedField
      label={t('policiesLibrary.formVariablePricingTitle', 'Variable Pricing Timetable')}
      {...props}
      hidden={!policy.parkingFeeType || _.includes(policy.parkingFeeType, 'fixed')}
    >
      <Timetable editable={editable} editPolicy={editPolicy} policy={policy} />
    </ThemedField>
  )
}
